import React, { useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  Box,
  Button,
  Typography,
  CircularProgress,
  FormControlLabel,
  Slider,
  Switch,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LinkIcon from '@mui/icons-material/Link';
import { useTranslation } from 'react-i18next';
import { ImageUploadService } from '../../../accounting/inventory/services/imageUploadService';
import RichTextEditor from '../../../common/RichTextEditor';

export type CustomTabSectionImagePosition = 'left' | 'right';
export type CustomTabSectionImageSize = 'small' | 'medium' | 'large' | 'xlarge' | 'equal';
export type CustomTabSectionImageAnimation = 'fade' | 'zoom' | 'slide' | 'none';

const SIZE_PRESETS: Array<{ value: CustomTabSectionImageSize; width?: number }> = [
  { value: 'small', width: 160 },
  { value: 'medium', width: 240 },
  { value: 'large', width: 400 },
  { value: 'equal' },
];

const ANIMATION_OPTIONS: CustomTabSectionImageAnimation[] = ['zoom', 'fade', 'slide', 'none'];

function isEqualSize(size?: CustomTabSectionImageSize): boolean {
  return size === 'equal' || size === 'xlarge';
}

function sizeLabelKey(value: CustomTabSectionImageSize): string {
  if (value === 'equal' || value === 'xlarge') return 'sectionImageSizeEqual';
  return `sectionImageSize${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

const WIDTH_MIN = 80;
const WIDTH_MAX = 640;
const HEIGHT_MIN = 60;
const HEIGHT_MAX = 480;

function clampPx(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

function resolveWidth(section: Pick<CustomTabSectionData, 'imageSize' | 'imageWidth'>): number {
  if (isEqualSize(section.imageSize)) return 400;
  const raw = Number(section.imageWidth);
  if (Number.isFinite(raw) && raw > 0) return clampPx(raw, WIDTH_MIN, WIDTH_MAX);
  const preset = SIZE_PRESETS.find((item) => item.value === section.imageSize);
  return preset?.width ?? 240;
}

function resolveHeight(section: Pick<CustomTabSectionData, 'imageHeight'>): number | undefined {
  const raw = Number(section.imageHeight);
  if (!Number.isFinite(raw) || raw <= 0) return undefined;
  return clampPx(raw, HEIGHT_MIN, HEIGHT_MAX);
}

export interface CustomTabSectionData {
  id: string;
  title: string;
  content: string;
  image?: string;
  imagePosition?: CustomTabSectionImagePosition;
  imageSize?: CustomTabSectionImageSize;
  imageWidth?: number | string;
  imageHeight?: number | string;
  imageAnimation?: CustomTabSectionImageAnimation;
}

interface CustomTabSectionFieldsProps {
  section: CustomTabSectionData;
  onUpdate: (field: keyof CustomTabSectionData, value: string) => void;
  titlePlaceholder?: string;
  contentPlaceholder?: string;
}

export function CustomTabSectionFields({
  section,
  onUpdate,
  titlePlaceholder,
  contentPlaceholder,
}: CustomTabSectionFieldsProps) {
  const { t } = useTranslation('salesSystem');
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [showCustomSize, setShowCustomSize] = useState(false);
  const savedWidth = resolveWidth(section);
  const savedHeight = resolveHeight(section);
  const [draftWidth, setDraftWidth] = useState(savedWidth);
  const [draftHeight, setDraftHeight] = useState(savedHeight || 180);

  useEffect(() => {
    setDraftWidth(savedWidth);
  }, [savedWidth]);

  useEffect(() => {
    if (savedHeight) setDraftHeight(savedHeight);
  }, [savedHeight]);

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      const [uploaded] = await ImageUploadService.uploadImages([file]);
      if (uploaded?.originalUrl) {
        onUpdate('image', uploaded.originalUrl);
        if (!section.imageSize) {
          onUpdate('imageSize', 'equal');
        }
        if (!section.imageAnimation) {
          onUpdate('imageAnimation', 'zoom');
        }
      }
    } catch (error) {
      console.error('Section image upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const chipSx = {
    textTransform: 'none' as const,
    px: 1.25,
    py: 0.25,
    fontSize: 12,
    lineHeight: 1.6,
    borderRadius: 1,
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={t('ecommerceAboutSettings.customTabs.sectionTitleOptional', {
            defaultValue: 'Bölüm Başlığı (Opsiyonel)',
          })}
          value={section.title}
          onChange={(e) => onUpdate('title', e.target.value)}
          variant="outlined"
          size="small"
          placeholder={titlePlaceholder}
          helperText={t('ecommerceAboutSettings.customTabs.sectionTitleOptionalHelper', {
            defaultValue: 'Boş bırakılabilir. Doluysa ana içerikte ve yan menüde görünür.',
          })}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        {section.image ? (
          <Box
            component="img"
            src={section.image}
            alt={section.title || t('ecommerceAboutSettings.customTabs.sectionImage')}
            sx={{
              width: '100%',
              maxHeight: 160,
              objectFit: 'cover',
              borderRadius: 1,
              mb: 1,
              border: '1px solid #e5e7eb',
            }}
          />
        ) : (
          <Box
            component="label"
            sx={{
              width: '100%',
              height: 120,
              border: '1px dashed #c5c9d1',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1,
              bgcolor: '#fafafa',
              cursor: 'pointer',
              gap: 0.5,
            }}
          >
            {uploading ? (
              <CircularProgress size={22} />
            ) : (
              <>
                <CloudUploadIcon fontSize="small" color="action" />
                <Typography variant="caption" color="text.secondary">
                  {t('ecommerceAboutSettings.customTabs.sectionImageDrop', {
                    defaultValue: 'Görsel yüklemek için tıklayın',
                  })}
                </Typography>
              </>
            )}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
                e.target.value = '';
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 0.75, mb: 1.25, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            component="label"
            startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
            disabled={uploading}
          >
            {uploading
              ? t('ecommerceCorporateHero.uploading')
              : t('ecommerceCorporateHero.uploadImage')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleUpload(file);
                e.target.value = '';
              }}
            />
          </Button>
          {section.image && (
            <Button
              size="small"
              color="inherit"
              startIcon={<DeleteOutlineIcon />}
              onClick={() => {
                onUpdate('image', '');
                onUpdate('imageWidth', '');
                onUpdate('imageHeight', '');
              }}
            >
              {t('ecommerceAboutSettings.customTabs.sectionImageRemove', {
                defaultValue: 'Kaldır',
              })}
            </Button>
          )}
          <Button
            size="small"
            color="inherit"
            startIcon={<LinkIcon />}
            onClick={() => setShowUrl((prev) => !prev)}
          >
            {t('ecommerceAboutSettings.customTabs.sectionImagePasteUrl', {
              defaultValue: 'URL',
            })}
          </Button>
        </Box>

        <Collapse in={showUrl}>
          <TextField
            fullWidth
            size="small"
            label={t('ecommerceAboutSettings.customTabs.sectionImageUrl')}
            value={section.image || ''}
            onChange={(e) => onUpdate('image', e.target.value)}
            placeholder="https://..."
            sx={{ mb: 1.25 }}
          />
        </Collapse>

        {section.image && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('ecommerceAboutSettings.customTabs.sectionImagePosition')}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={section.imagePosition || 'left'}
                onChange={(_e, value) => {
                  if (value) onUpdate('imagePosition', value);
                }}
              >
                <ToggleButton value="left" sx={chipSx}>
                  {t('ecommerceAboutSettings.customTabs.sectionImageLeftShort', {
                    defaultValue: 'Solda',
                  })}
                </ToggleButton>
                <ToggleButton value="right" sx={chipSx}>
                  {t('ecommerceAboutSettings.customTabs.sectionImageRightShort', {
                    defaultValue: 'Sağda',
                  })}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('ecommerceAboutSettings.customTabs.sectionImageSize')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {SIZE_PRESETS.map((preset) => {
                  const selected = isEqualSize(preset.value)
                    ? isEqualSize(section.imageSize)
                    : !isEqualSize(section.imageSize) && section.imageSize === preset.value;
                  return (
                    <Button
                      key={preset.value}
                      size="small"
                      variant={selected ? 'contained' : 'outlined'}
                      sx={chipSx}
                      onClick={() => {
                        if (preset.value === 'equal') {
                          onUpdate('imageSize', 'equal');
                          onUpdate('imageWidth', '');
                          onUpdate('imageHeight', '');
                          setShowCustomSize(false);
                          return;
                        }
                        setDraftWidth(preset.width ?? 240);
                        onUpdate('imageSize', preset.value);
                        onUpdate('imageWidth', String(preset.width));
                      }}
                    >
                      {t(`ecommerceAboutSettings.customTabs.${sizeLabelKey(preset.value)}`)}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {t('ecommerceAboutSettings.customTabs.sectionImageAnimation')}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                fullWidth
                value={section.imageAnimation || 'zoom'}
                onChange={(_e, value) => {
                  if (value) onUpdate('imageAnimation', value);
                }}
              >
                {ANIMATION_OPTIONS.map((option) => (
                  <ToggleButton key={option} value={option} sx={chipSx}>
                    {t(`ecommerceAboutSettings.customTabs.sectionImageAnimation${option.charAt(0).toUpperCase()}${option.slice(1)}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {!isEqualSize(section.imageSize) && (
              <Box>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setShowCustomSize((prev) => !prev)}
                  sx={{ px: 0, minWidth: 0, textTransform: 'none' }}
                >
                  {showCustomSize
                    ? t('ecommerceAboutSettings.customTabs.sectionImageCustomSizeHide', {
                        defaultValue: 'Özel ölçüyü gizle',
                      })
                    : t('ecommerceAboutSettings.customTabs.sectionImageCustomSize', {
                        defaultValue: 'Özel ölçü',
                      })}
                </Button>
                <Collapse in={showCustomSize}>
                  <Box sx={{ pt: 1 }}>
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption">
                          {t('ecommerceAboutSettings.customTabs.sectionImageWidth')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {draftWidth}px
                        </Typography>
                      </Box>
                      <Slider
                        size="small"
                        min={WIDTH_MIN}
                        max={WIDTH_MAX}
                        step={4}
                        value={draftWidth}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}px`}
                        onChange={(_e, value) => {
                          const next = Array.isArray(value) ? value[0] : value;
                          setDraftWidth(next);
                        }}
                        onChangeCommitted={(_e, value) => {
                          const next = Array.isArray(value) ? value[0] : value;
                          onUpdate('imageWidth', String(next));
                        }}
                      />
                    </Box>
                    <FormControlLabel
                      sx={{ ml: 0, alignItems: 'center' }}
                      control={
                        <Switch
                          size="small"
                          checked={Boolean(savedHeight)}
                          onChange={(e) =>
                            onUpdate(
                              'imageHeight',
                              e.target.checked ? String(draftHeight || 180) : ''
                            )
                          }
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {t('ecommerceAboutSettings.customTabs.sectionImageHeightLock')}
                        </Typography>
                      }
                    />
                    {savedHeight ? (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                          <Typography variant="caption">
                            {t('ecommerceAboutSettings.customTabs.sectionImageHeight')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {draftHeight}px
                          </Typography>
                        </Box>
                        <Slider
                          size="small"
                          min={HEIGHT_MIN}
                          max={HEIGHT_MAX}
                          step={4}
                          value={draftHeight}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(value) => `${value}px`}
                          onChange={(_e, value) => {
                            const next = Array.isArray(value) ? value[0] : value;
                            setDraftHeight(next);
                          }}
                          onChangeCommitted={(_e, value) => {
                            const next = Array.isArray(value) ? value[0] : value;
                            onUpdate('imageHeight', String(next));
                          }}
                        />
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('ecommerceAboutSettings.customTabs.sectionImageHeightAuto')}
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            )}

            {isEqualSize(section.imageSize) && (
              <Typography variant="caption" color="text.secondary">
                {t('ecommerceAboutSettings.customTabs.sectionImageEqualHint')}
              </Typography>
            )}
          </Box>
        )}
      </Grid>

      <Grid item xs={12} md={8}>
        <RichTextEditor
          label={t('ecommerceAboutSettings.customTabs.sectionContent')}
          value={section.content}
          onChange={(html) => onUpdate('content', html)}
          placeholder={contentPlaceholder}
          minHeight={180}
        />
      </Grid>
    </Grid>
  );
}
